import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { useTranslation } from '@/hooks/useTranslation';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FaWhatsapp } from 'react-icons/fa';
import AddressSearch from '@/components/AddressSearch';
import ImageUpload from '@/components/ImageUpload';
import WhatsAppEmbeddedSignup from '@/components/WhatsAppEmbeddedSignup';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  Store,
  MapPin,
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  Package,
  CreditCard,
  Bot,
  Bell,
  Rocket,
  Copy,
  ExternalLink,
  Settings,
  Upload,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  X,
  FileSpreadsheet,
  Wallet,
  Building2,
  Home,
  ShoppingCart,
  Headphones,
  Link2,
  Lightbulb,
  Zap,
  Cloud,
  AlertTriangle,
  BarChart3,
  FileEdit,
  Landmark,
  DollarSign,
  Send,
  Smartphone,
  Mail,
  Download,
  Layers,
  Pencil,
  UploadCloud
} from 'lucide-react';

const TOTAL_STEPS = 8;

// Business categories will be translated via t() function
const BUSINESS_CATEGORY_KEYS = [
  'fashion',
  'food',
  'health',
  'electronics',
  'home',
  'sports',
  'automotive',
  'books',
  'toys',
  'services',
  'other'
];

// AI styles will be translated via t() function
const AI_STYLE_KEYS = [
  { id: 'friendly', labelKey: 'friendly', exampleKey: 'friendlyExample' },
  { id: 'professional', labelKey: 'professional', exampleKey: 'professionalExample' },
  { id: 'casual', labelKey: 'casual', exampleKey: 'casualExample' }
];

// Progress Bar Component
const ProgressBar = ({ currentStep, totalSteps, t }) => {
  return (
    <div className="w-full mb-4 sm:mb-6">
      <div className="flex items-center justify-between mb-2">
        {Array.from({ length: totalSteps }, (_, i) => (
          <React.Fragment key={i}>
            <div
              className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-all shrink-0 ${
                i + 1 < currentStep
                  ? 'bg-indigo-500 text-white'
                  : i + 1 === currentStep
                  ? 'bg-indigo-500 text-white ring-2 sm:ring-4 ring-blue-100 dark:ring-blue-900'
                  : 'bg-slate-200 dark:bg-zinc-800 text-slate-500'
              } dark:text-zinc-400`}
            >
              {i + 1 < currentStep ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : i + 1}
            </div>
            {i < totalSteps - 1 && (
              <div
                className={`flex-1 h-0.5 sm:h-1 mx-0.5 sm:mx-1 rounded ${
                  i + 1 < currentStep ? 'bg-indigo-500' : 'bg-slate-200 dark:bg-zinc-800'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      <p className="text-center text-xs sm:text-sm text-slate-500 dark:text-zinc-400">
        {t('onboarding.step')} {currentStep} {t('onboarding.of')} {totalSteps}
      </p>
    </div>
  );
};

// Step 1: Business Info + Address
const BUSINESS_TYPE_OPTIONS = [
  { value: 'physical', label: 'Produk Fisik', desc: 'Dikirim pakai kurir (butuh alamat & ongkir)', icon: Package },
  { value: 'digital', label: 'Produk Digital', desc: 'File, e-book, template, kode, akses grup', icon: Download },
  { value: 'both', label: 'Keduanya', desc: 'Jual produk fisik dan digital sekaligus', icon: Layers },
];

const Step1Business = ({ data, setData, onNext, t }) => {
  const [errors, setErrors] = useState({});
  const businessType = data.business_type || 'physical';
  const isDigitalOnly = businessType === 'digital';

  const validate = () => {
    const newErrors = {};
    if (!data.store_name) newErrors.store_name = t('onboarding.storeNameRequired');
    if (!data.category) newErrors.category = t('onboarding.categoryRequired');
    // Toko yang mengirim barang fisik WAJIB punya alamat — dipakai sebagai titik asal ongkir.
    if (!isDigitalOnly) {
      if (!(data.street_address || data.address)) {
        newErrors.street_address = 'Alamat jalan wajib diisi untuk hitung ongkir';
      }
      if (!data.destination_id || !(data.city_name || data.city)) {
        newErrors.destination = 'Pilih kelurahan/kecamatan lewat pencarian alamat';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  // Handle Smart Address Search selection
  const handleAddressSelect = (locationData) => {
    if (locationData) {
      setData({
        ...data,
        destination_id: locationData.destination_id,
        subdistrict_name: locationData.subdistrict_name,
        district_name: locationData.district_name,
        city_name: locationData.city_name,
        city: locationData.city_name, // Legacy field
        province_name: locationData.province_name,
        postal_code: locationData.postal_code,
        full_address: `${data.street_address || data.address || ''}, ${locationData.subdistrict_name}, ${locationData.district_name}, ${locationData.city_name}, ${locationData.province_name} ${locationData.postal_code}`
      });
    } else {
      setData({
        ...data,
        destination_id: '',
        subdistrict_name: '',
        district_name: '',
        city_name: '',
        province_name: '',
        postal_code: ''
      });
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 dark:bg-indigo-950/40 rounded-xl sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Store className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">{t('onboarding.aboutBusiness')}</h2>
        <p className="text-sm sm:text-base text-slate-600 dark:text-zinc-400 mt-2">{t('onboarding.businessInfoDesc')}</p>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <div>
          <Label className="text-sm">{t('onboarding.storeName')} <span className="text-red-500">*</span></Label>
          <Input
            value={data.store_name || ''}
            onChange={(e) => setData({ ...data, store_name: e.target.value })}
            placeholder={t('onboarding.storeNamePlaceholder')}
            className={errors.store_name ? 'border-red-500' : ''}
          />
          {errors.store_name && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.store_name}</p>}
        </div>

        <div>
          <Label className="text-sm">Jenis produk yang dijual <span className="text-red-500">*</span></Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2" data-testid="business-type-selector">
            {BUSINESS_TYPE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = businessType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setData({ ...data, business_type: opt.value })}
                  data-testid={`business-type-${opt.value}`}
                  className={`text-left rounded-xl border p-3 transition-colors ${active
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 dark:border-indigo-500'
                    : 'border-slate-200 hover:border-slate-300 dark:border-zinc-800 dark:hover:border-zinc-700'}`}
                >
                  <Icon className={`w-4 h-4 mb-1.5 ${active ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-zinc-400'}`} />
                  <p className={`text-sm font-medium ${active ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-800 dark:text-zinc-200'}`}>{opt.label}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5 leading-snug">{opt.desc}</p>
                </button>
              );
            })}
          </div>
          {isDigitalOnly && (
            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-2" data-testid="business-type-digital-note">
              Mode digital: fitur ongkir, alamat pengiriman, dan resi disembunyikan. AI hanya meminta nama & email customer.
            </p>
          )}
        </div>

        <div>
          <Label className="text-sm">{t('onboarding.category')} <span className="text-red-500">*</span></Label>
          <Select value={data.category || ''} onValueChange={(v) => setData({ ...data, category: v })}>
            <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
              <SelectValue placeholder={t('onboarding.selectCategory')} />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_CATEGORY_KEYS.map((catKey) => (
                <SelectItem key={catKey} value={catKey}>{t(`onboarding.categories.${catKey}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.category}</p>}
        </div>

        <div>
          <Label className="text-sm">{t('onboarding.shortDescription')}</Label>
          <Textarea
            value={data.description || ''}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            placeholder={t('onboarding.descriptionPlaceholder')}
            rows={2}
          />
        </div>

        {/* Address Section with Smart Search — tidak relevan untuk toko produk digital */}
        {!isDigitalOnly && (
        <div className="pt-3 sm:pt-4 border-t border-slate-200 dark:border-zinc-800">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
            <Label className="text-sm sm:text-base font-medium">Alamat Toko <span className="text-red-500">*</span></Label>
          </div>
          
          {/* Street Address Input */}
          <div className="mb-4">
            <Label className="text-sm flex items-center gap-2 mb-2">
              <Home className="w-4 h-4" />
              Alamat Jalan <span className="text-red-500">*</span>
            </Label>
            <Input
              value={data.street_address || data.address || ''}
              onChange={(e) => setData({ ...data, street_address: e.target.value, address: e.target.value })}
              placeholder="Contoh: Jl. Sudirman No. 123, Gedung ABC Lt. 5"
              className={`dark:bg-zinc-800 ${errors.street_address ? 'border-red-500' : ''}`}
              data-testid="onboarding-street-address"
            />
            {errors.street_address && (
              <p className="text-red-500 text-xs sm:text-sm mt-1" data-testid="onboarding-street-address-error">{errors.street_address}</p>
            )}
          </div>
          
          {/* Smart Address Search */}
          <AddressSearch
            value={{
              destination_id: data.destination_id,
              subdistrict_name: data.subdistrict_name,
              district_name: data.district_name,
              city_name: data.city_name || data.city,
              province_name: data.province_name,
              postal_code: data.postal_code
            }}
            onChange={handleAddressSelect}
          />
          {errors.destination && (
            <p className="text-red-500 text-xs sm:text-sm mt-1" data-testid="onboarding-destination-error">{errors.destination}</p>
          )}
          <p className="text-xs text-slate-500 dark:text-zinc-400 mt-2">
            Alamat ini jadi titik asal perhitungan ongkir, jadi wajib diisi untuk produk fisik.
          </p>
        </div>
        )}
      </div>

      <Button onClick={handleNext} className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm sm:text-base" data-testid="onboarding-next-btn">
        {t('onboarding.continue')}
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Step 2: Connect Channels
const Step2Channels = ({ data, setData, onNext, onBack, t, fbPages, setFbPages, showPageSelector, setShowPageSelector, selectingPage, setSelectingPage, shouldFetchFbPages, setShouldFetchFbPages, messengerConnected, setMessengerConnected, messengerPageName, setMessengerPageName, instagramConnected, setInstagramConnected, instagramName, setInstagramName }) => {
  const [showWaModal, setShowWaModal] = useState(false);
  const [waMode, setWaMode] = useState(data.whatsapp_mode || 'nerava');
  const [ownPhone, setOwnPhone] = useState('');
  const [fbSdkLoaded, setFbSdkLoaded] = useState(false);
  
  // QR Code states
  const [qrLoading, setQrLoading] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [qrSessionId, setQrSessionId] = useState(null);
  const [qrStatus, setQrStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [hasShownConnectedToast, setHasShownConnectedToast] = useState(false); // Prevent duplicate toasts

  // Check Messenger & Instagram connection status on mount
  useEffect(() => {
    // Load Facebook SDK for Messenger OAuth (mobile-friendly)
    if (!document.getElementById('facebook-jssdk')) {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: process.env.REACT_APP_FACEBOOK_APP_ID || '4201103220206383',
          autoLogAppEvents: true,
          xfbml: false,
          version: 'v21.0'
        });
        setFbSdkLoaded(true);
      };
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    } else if (window.FB) {
      setFbSdkLoaded(true);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    const checkMessengerStatus = async () => {
      try {
        const response = await axios.get(`${API}/messenger/status`, { headers });
        if (response.data.connected) {
          setMessengerConnected(true);
          setMessengerPageName(response.data.page_name || 'Facebook Page');
        }
      } catch (error) {
        console.log('Messenger not connected');
      }
    };

    const checkInstagramStatus = async () => {
      try {
        const response = await axios.get(`${API}/instagram/status`, { headers });
        if (response.data.connected) {
          setInstagramConnected(true);
          setInstagramName(response.data.platform_name || 'Instagram Account');
        }
      } catch (error) {
        console.log('Instagram not connected');
      }
    };

    checkMessengerStatus();
    checkInstagramStatus();
  }, []);

  // Fetch Facebook pages when triggered by OAuth callback (action=select-page)
  useEffect(() => {
    if (shouldFetchFbPages) {
      fetchFacebookPages();
      setShouldFetchFbPages(false);
    }
  }, [shouldFetchFbPages]);

  useEffect(() => {
    if (!data.whatsapp_connected) {
      setData({
        ...data,
        whatsapp_connected: true,
        whatsapp_mode: 'nerava',
        whatsapp_phone: '+6285111444413'
      });
    }
    
    // Cleanup polling on unmount
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);
  
  // Start QR Code session
  const startQRSession = async () => {
    setQrLoading(true);
    setHasShownConnectedToast(false); // Reset toast flag for new session
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp-qr/start-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'connected') {
        // Already connected
        setQrStatus('connected');
        setData({
          ...data,
          whatsapp_connected: true,
          whatsapp_mode: 'qr_code',
          whatsapp_phone: response.data.phone_number,
          whatsapp_qr_session: response.data.session_id
        });
        if (!hasShownConnectedToast) {
          toast.success(t('onboarding.waConnected') || 'WhatsApp sudah terhubung!');
          setHasShownConnectedToast(true);
        }
      } else {
        setQrCode(response.data.qr_code);
        setQrSessionId(response.data.session_id);
        setQrStatus('pending');
        
        // Start polling for status
        const interval = setInterval(() => checkQRStatus(response.data.session_id), 3000);
        setPollingInterval(interval);
      }
    } catch (error) {
      toast.error('Gagal memulai sesi QR. Coba lagi.');
      console.error('QR session error:', error);
    } finally {
      setQrLoading(false);
    }
  };
  
  // Check QR status
  const checkQRStatus = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp-qr/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newStatus = response.data.status;
      
      // Update QR code if new one is available
      if (response.data.qr_code && newStatus === 'qr_ready') {
        setQrCode(response.data.qr_code);
      }
      
      if (newStatus === 'connected' && qrStatus !== 'connected') {
        if (pollingInterval) clearInterval(pollingInterval);
        setPollingInterval(null);
        setQrStatus('connected');
        setData({
          ...data,
          whatsapp_connected: true,
          whatsapp_mode: 'qr_code',
          whatsapp_phone: response.data.phone_number,
          whatsapp_phone_name: response.data.phone_name,
          whatsapp_qr_session: sessionId
        });
        if (!hasShownConnectedToast) {
          toast.success('WhatsApp berhasil terhubung!');
          setHasShownConnectedToast(true);
        }
        setShowWaModal(false);
      } else if (newStatus === 'expired' || newStatus === 'disconnected') {
        if (pollingInterval) clearInterval(pollingInterval);
        setPollingInterval(null);
        setQrCode(null);
        setQrStatus(newStatus);
        toast.error('Sesi berakhir. Silakan generate QR baru.');
      } else {
        setQrStatus(newStatus);
      }
    } catch (error) {
      console.error('Status check error:', error);
    }
  };
  
  const handleConnectInstagram = () => {
    // Instagram Business Login (direct Instagram OAuth)
    const instagramAppId = '1183763387300782';  // Instagram App ID
    const redirectUri = 'https://nerava.id/api/instagram/oauth/callback';
    
    // Required permissions for Instagram Business API
    const scopes = [
      'instagram_business_basic',
      'instagram_business_manage_messages'
    ].join('%2C');  // URL encoded comma
    
    // Get user_id from localStorage token
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Silakan login terlebih dahulu');
      return;
    }
    
    // Decode token to get user_id (simple base64 decode for JWT payload)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.user_id;
      
      // Encode return_to with step number in state (format: user_id:base64_return_to)
      const returnToEncoded = btoa('/onboarding?step=2');
      const state = `${userId}:${returnToEncoded}`;
      
      // Instagram Business Login OAuth URL
      const instagramOAuthUrl = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${instagramAppId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${scopes}&state=${state}`;
      
      // Always navigate in same tab so user returns to onboarding seamlessly
      window.location.href = instagramOAuthUrl;
    } catch (e) {
      toast.error('Gagal memproses autentikasi. Silakan coba lagi.');
    }
  };

  const handleConnectMessenger = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Silakan login terlebih dahulu');
      return;
    }

    // Use FB SDK for all devices (mobile + desktop)
    if (window.FB) {
      window.FB.login((response) => {
        if (response.authResponse) {
          const accessToken = response.authResponse.accessToken;
          const hdrs = { Authorization: `Bearer ${token}` };
          axios.post(`${API}/messenger/oauth/sdk-login`, 
            { access_token: accessToken }, 
            { headers: hdrs }
          ).then(res => {
            if (res.data.success && res.data.pages) {
              setFbPages(res.data.pages);
              setShowPageSelector(true);
              toast.success('Login Facebook berhasil! Pilih halaman yang ingin dihubungkan.');
            }
          }).catch(err => {
            console.error('FB SDK login error:', err);
            toast.error(err.response?.data?.detail || 'Gagal menghubungkan Facebook');
          });
        } else {
          toast.error('Login Facebook dibatalkan');
        }
      }, {
        scope: 'pages_show_list,pages_messaging,pages_manage_metadata',
        return_scopes: true
      });
    } else {
      toast.error('Facebook SDK belum dimuat. Silakan refresh halaman dan coba lagi.');
    }
  };

  // Fetch Facebook pages for page selection after OAuth callback
  const fetchFacebookPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messenger/oauth/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pages = response.data.pages || [];
      if (pages.length > 0) {
        setFbPages(pages);
        setShowPageSelector(true);
      } else {
        toast.error('Tidak ada halaman Facebook yang ditemukan.');
      }
    } catch (e) {
      toast.error('Gagal mengambil daftar halaman Facebook.');
    }
  };

  // Handle page selection for Messenger
  const handleSelectFbPage = async (pageId, pageName) => {
    setSelectingPage(true);
    try {
      const token = localStorage.getItem('token');
      
      // Try to find existing chatbot (may not exist yet during onboarding)
      let chatbotId = null;
      try {
        const chatbotsResponse = await axios.get(`${API}/chatbots`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const chatbots = chatbotsResponse.data || [];
        if (chatbots.length > 0) chatbotId = chatbots[0].id;
      } catch (e) { /* No chatbot yet, that's ok during onboarding */ }

      const payload = { page_id: pageId };
      if (chatbotId) payload.chatbot_id = chatbotId;

      const response = await axios.post(`${API}/messenger/oauth/select-page`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMessengerConnected(true);
        setMessengerPageName(pageName);
        setShowPageSelector(false);
        setFbPages([]);
        toast.success(`Facebook Page "${pageName}" berhasil dihubungkan!`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghubungkan halaman Facebook.');
    }
    setSelectingPage(false);
  };

  const handleSaveWaSettings = () => {
    if (waMode === 'qr_code') {
      // QR mode - connection handled separately
      if (qrStatus !== 'connected') {
        toast.error('Scan QR code terlebih dahulu');
        return;
      }
      setShowWaModal(false);
    } else if (waMode === 'cloud_api') {
      // Cloud API - check if already connected via Embedded Signup
      if (data.whatsapp_own_status === 'connected') {
        setShowWaModal(false);
      } else {
        toast.error('Klik "Hubungkan dengan Meta" untuk menghubungkan WhatsApp Cloud API');
        return;
      }
    } else {
      setData({
        ...data,
        whatsapp_mode: 'nerava',
        whatsapp_own_phone: null
      });
      setShowWaModal(false);
    }
    
    // Clear QR states when closing
    if (pollingInterval) clearInterval(pollingInterval);
    setQrCode(null);
    setQrSessionId(null);
    setQrStatus(null);
  };
  
  const handleCloseWaModal = () => {
    if (pollingInterval) clearInterval(pollingInterval);
    setQrCode(null);
    setQrSessionId(null);
    setQrStatus(null);
    setShowWaModal(false);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 dark:bg-green-900/30 rounded-xl sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">Hubungkan Channel</h2>
        <p className="text-sm sm:text-base text-slate-600 dark:text-zinc-400 mt-2">Pilih dari mana customer akan menghubungi Anda</p>
      </div>

      <div className="space-y-3">
        {/* WhatsApp Status Card */}
        <div className="border-2 border-green-500 bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900 dark:text-zinc-50">WhatsApp Business</span>
                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {data.whatsapp_mode === 'qr_code' ? 'QR Connected' : 'Aktif'}
                  </span>
                </div>
                <p className="text-sm text-slate-600 dark:text-zinc-400">
                  {data.whatsapp_mode === 'qr_code' 
                    ? `${data.whatsapp_phone || 'Nomor Anda'} (via QR Code)` 
                    : data.whatsapp_mode === 'cloud_api'
                    ? `${data.whatsapp_own_phone || 'Nomor Anda'} (Cloud API)`
                    : `${data.whatsapp_phone || '+62 851-1144-4413'} (Nomor Nerava)`
                  }
                </p>
                {data.whatsapp_mode === 'cloud_api' && data.whatsapp_own_status === 'pending' && (
                  <p className="text-xs text-amber-600 mt-1 dark:text-amber-400">
                    ⏳ Nomor Anda sedang diverifikasi (1-3 hari kerja)
                  </p>
                )}
                {data.whatsapp_mode === 'qr_code' && (
                  <p className="text-xs text-green-600 mt-1 dark:text-green-400">
                    <Check className="w-3 h-3 inline mr-1" />Nomor tetap bisa dipakai di HP
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="flex-shrink-0 bg-white dark:bg-zinc-900 border-green-600 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-900/40 gap-1.5 shadow-sm"
              onClick={() => setShowWaModal(true)}
              data-testid="onboarding-change-wa-btn"
            >
              <Settings className="w-3.5 h-3.5" />
              Ubah
            </Button>
          </div>

          {/* Penjelasan singkat nomor bersama Nerava */}
          {(data.whatsapp_mode === 'nerava' || !data.whatsapp_mode) && (
            <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800" data-testid="onboarding-nerava-number-info">
              <p className="text-xs font-semibold text-green-900 dark:text-green-200 mb-1.5">
                Kenapa nomornya bukan nomor saya?
              </p>
              <ul className="text-xs text-green-800 dark:text-green-300 space-y-1 list-disc pl-4">
                <li>
                  Ini <strong>nomor WhatsApp bersama Nerava</strong> — toko Anda langsung aktif tanpa daftar ke Meta atau beli nomor baru.
                </li>
                <li>
                  Toko Anda punya <strong>kode unik</strong>, jadi chat dari link toko Anda hanya masuk ke AI Anda. Link + kodenya ada di langkah terakhir dan menu <strong>Channel</strong>.
                </li>
              </ul>
              <p className="text-xs text-green-700 dark:text-green-400 mt-1.5">
                Mau pakai nomor sendiri? Klik <strong>Ubah</strong>.
              </p>
            </div>
          )}
        </div>

        {/* Instagram */}
        <div className={`border rounded-xl p-4 ${instagramConnected ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' : 'border-slate-200 dark:border-zinc-800'}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center ${instagramConnected ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30'}`}>
                <Instagram className={`w-5 h-5 ${instagramConnected ? 'text-green-600' : 'text-pink-600'} dark:text-green-400`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 dark:text-zinc-50">Instagram DM</span>
                  {instagramConnected && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 rounded-full whitespace-nowrap">
                      <Check className="w-3 h-3" /> Aktif
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 truncate dark:text-zinc-400">
                  {instagramConnected ? instagramName : 'Auto-reply DM dari akun Instagram'}
                </p>
              </div>
            </div>
            {instagramConnected ? (
              <Button variant="outline" size="sm" className="flex-shrink-0 gap-1.5" onClick={handleConnectInstagram} data-testid="onboarding-change-ig-btn">
                <Settings className="w-3.5 h-3.5" />
                Ubah
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="flex-shrink-0" onClick={handleConnectInstagram}>
                Hubungkan
              </Button>
            )}
          </div>
        </div>

        {/* Messenger */}
        <div className={`border rounded-xl p-4 ${messengerConnected ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' : 'border-slate-200 dark:border-zinc-800'}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center ${messengerConnected ? 'bg-green-100 dark:bg-green-900/30' : 'bg-indigo-100 dark:bg-indigo-950/40'}`}>
                <Facebook className={`w-5 h-5 ${messengerConnected ? 'text-green-600' : 'text-indigo-600 dark:text-indigo-400'} dark:text-green-400`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 dark:text-zinc-50">Facebook Messenger</span>
                  {messengerConnected && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 rounded-full whitespace-nowrap">
                      <Check className="w-3 h-3" /> Aktif
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 truncate dark:text-zinc-400">
                  {messengerConnected ? messengerPageName : 'Auto-reply chat dari Facebook Page'}
                </p>
              </div>
            </div>
            {messengerConnected ? (
              <Button variant="outline" size="sm" className="flex-shrink-0 gap-1.5" onClick={handleConnectMessenger} disabled={showPageSelector} data-testid="onboarding-change-messenger-btn">
                <Settings className="w-3.5 h-3.5" />
                Ubah
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="flex-shrink-0" onClick={handleConnectMessenger} disabled={showPageSelector}>
                Hubungkan
              </Button>
            )}
          </div>
        </div>

        {/* Facebook Page Selection Modal */}
        {showPageSelector && fbPages.length > 0 && (
          <div className="border-2 border-indigo-300 dark:border-indigo-600 rounded-xl p-4 bg-indigo-50/50 dark:bg-blue-900/10">
            <div className="flex items-center gap-2 mb-3">
              <Facebook className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <span className="font-semibold text-slate-900 dark:text-zinc-50">Pilih Halaman Facebook</span>
            </div>
            <p className="text-sm text-slate-500 mb-3 dark:text-zinc-400">Pilih halaman yang ingin dihubungkan dengan chatbot:</p>
            <div className="space-y-2">
              {fbPages.map((pg) => (
                <button
                  key={pg.id}
                  onClick={() => handleSelectFbPage(pg.id, pg.name)}
                  disabled={selectingPage}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-zinc-700 hover:border-blue-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors text-left disabled:opacity-50 dark:bg-indigo-950/40"
                  data-testid={`fb-page-${pg.id}`}
                >
                  {pg.picture ? (
                    <img src={pg.picture} alt={pg.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-blue-900/50 flex items-center justify-center">
                      <Facebook className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                  )}
                  <span className="font-medium text-slate-900 dark:text-zinc-50">{pg.name}</span>
                  {selectingPage && <span className="ml-auto text-xs text-blue-500">Menghubungkan...</span>}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setShowPageSelector(false); setFbPages([]); }}
              className="mt-2 text-sm text-slate-500 hover:text-slate-600 dark:text-zinc-400"
            >
              Batal
            </button>
          </div>
        )}

        {/* Website Chat */}
        <div className="border border-slate-200 dark:border-zinc-800 rounded-xl p-4 opacity-60">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-950/40 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <span className="font-medium text-slate-900 dark:text-zinc-50">Website Chat</span>
                <p className="text-sm text-slate-500 dark:text-zinc-400">Embed chatbot di website Anda</p>
              </div>
            </div>
            <Button variant="outline" size="sm" disabled>
              Setup Nanti
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-2.5 sm:p-3">
        <p className="text-xs sm:text-sm text-blue-800 dark:text-indigo-200">
          <Lightbulb className="w-4 h-4 inline mr-1 text-indigo-600 dark:text-indigo-400" />WhatsApp sudah otomatis aktif! Channel lain bisa dihubungkan sekarang atau nanti di menu Settings.
        </p>
      </div>

      <div className="flex gap-2 sm:gap-3">
        <Button variant="outline" onClick={onBack} className="gap-1 sm:gap-2 text-sm px-3 sm:px-4">
          <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Kembali</span>
          <span className="sm:hidden">Back</span>
        </Button>
        <Button onClick={onNext} className="flex-1 gap-1 sm:gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="onboarding-next-btn">
          Lanjut
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </Button>
      </div>

      {/* WhatsApp Settings Modal - 3 Options */}
      <Dialog open={showWaModal} onOpenChange={handleCloseWaModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              Pilih Metode WhatsApp
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Option 1: Nerava Shared Number */}
            <div
              className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                waMode === 'nerava'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300'
              }`}
              onClick={() => setWaMode('nerava')}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  waMode === 'nerava' ? 'border-green-500 bg-green-500' : 'border-slate-300'
                }`}>
                  {waMode === 'nerava' && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 dark:text-zinc-50"><Zap className="w-4 h-4 inline mr-1" />Nomor Nerava</p>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full dark:text-green-300 dark:bg-green-950/40">Rekomendasi</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-zinc-400">+62 851-1144-4413</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs flex items-center gap-1 text-green-600 dark:text-green-400"><Check className="w-3 h-3" /> Langsung aktif</span>
                    <span className="text-xs flex items-center gap-1 text-green-600 dark:text-green-400"><Check className="w-3 h-3" /> Gratis</span>
                    <span className="text-xs flex items-center gap-1 text-green-600 dark:text-green-400"><Check className="w-3 h-3" /> Support Broadcast</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 dark:text-zinc-400">
                    Pakai nomor WA Nerava dengan keyword unik untuk routing ke toko Anda. Cocok untuk mulai cepat.
                  </p>
                </div>
              </div>
            </div>

            {/* Option 2: QR Code - Hidden: fitur QR code dinonaktifkan sementara */}
            {false && (
            <div>QR Code option hidden</div>
            )}

            {/* Option 3: Cloud API (Own Number) */}
            <div
              className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                waMode === 'cloud_api'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300'
              }`}
              onClick={() => setWaMode('cloud_api')}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  waMode === 'cloud_api' ? 'border-green-500 bg-green-500' : 'border-slate-300'
                }`}>
                  {waMode === 'cloud_api' && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 dark:text-zinc-50"><Cloud className="w-4 h-4 inline mr-1" />Cloud API (Nomor Sendiri)</p>
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full dark:text-purple-300 dark:bg-purple-950/40">Official</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-zinc-400">Daftarkan nomor WA bisnis Anda via Meta</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs flex items-center gap-1 text-green-600 dark:text-green-400"><Check className="w-3 h-3" /> Fitur lengkap</span>
                    <span className="text-xs flex items-center gap-1 text-green-600 dark:text-green-400"><Check className="w-3 h-3" /> Support Broadcast</span>
                    <span className="text-xs flex items-center gap-1 text-green-600 dark:text-green-400"><Check className="w-3 h-3" /> Stabil (server Meta)</span>
                  </div>
                  
                  {waMode === 'cloud_api' && (
                    <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                      <WhatsAppEmbeddedSignup
                        compact={true}
                        onSuccess={(connectionData) => {
                          setData({
                            ...data,
                            whatsapp_connected: true,
                            whatsapp_mode: 'cloud_api',
                            whatsapp_own_phone: connectionData?.phone_display_number || '',
                            whatsapp_own_status: 'connected'
                          });
                          toast.success('WhatsApp Cloud API berhasil terhubung!');
                        }}
                        onError={(err) => {
                          toast.error('Gagal menghubungkan WhatsApp: ' + (err?.message || 'Coba lagi'));
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="mt-4 p-3 bg-slate-50 dark:bg-zinc-900 rounded-lg">
            <p className="text-xs font-medium text-slate-700 dark:text-zinc-300 mb-2"><BarChart3 className="w-3 h-3 inline mr-1" />Perbandingan:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 dark:text-zinc-400">
                    <th className="text-left py-1">Fitur</th>
                    <th className="text-center py-1">Nerava</th>
                    <th className="text-center py-1">QR Code</th>
                    <th className="text-center py-1">Cloud API</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600 dark:text-zinc-400">
                  <tr>
                    <td className="py-1">Setup Time</td>
                    <td className="text-center">Instant</td>
                    <td className="text-center">1 menit</td>
                    <td className="text-center">~5 menit</td>
                  </tr>
                  <tr>
                    <td className="py-1">Nomor di HP</td>
                    <td className="text-center">-</td>
                    <td className="text-center"><Check className="w-3 h-3 inline text-green-500" /></td>
                    <td className="text-center"><X className="w-3 h-3 inline text-red-500" /></td>
                  </tr>
                  <tr>
                    <td className="py-1">Broadcast/Blast</td>
                    <td className="text-center"><Check className="w-3 h-3 inline text-green-500" /></td>
                    <td className="text-center"><AlertTriangle className="w-3 h-3 inline text-amber-500" /></td>
                    <td className="text-center"><Check className="w-3 h-3 inline text-green-500" /></td>
                  </tr>
                  <tr>
                    <td className="py-1">Stabilitas</td>
                    <td className="text-center">★★★</td>
                    <td className="text-center">★★</td>
                    <td className="text-center">★★★</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={handleCloseWaModal} className="flex-1">
              Batal
            </Button>
            <Button 
              onClick={handleSaveWaSettings} 
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={(waMode === 'qr_code' && qrStatus !== 'connected' && qrCode) || (waMode === 'cloud_api' && data.whatsapp_own_status !== 'connected')}
            >
              {waMode === 'qr_code' && qrStatus !== 'connected' && qrCode 
                ? 'Scan QR dulu...' 
                : waMode === 'cloud_api' && data.whatsapp_own_status !== 'connected'
                  ? 'Hubungkan WA dulu...'
                  : 'Simpan'
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Step 3: Add Products
const DIGITAL_TYPE_OPTIONS = [
  { value: 'file', label: 'File' },
  { value: 'license_code', label: 'Kode Lisensi' },
  { value: 'link_access', label: 'Link Akses' },
];

const emptyProduct = (fulfillment) => ({
  name: '', price: '', description: '', stock: '', variants: [], image_url: '',
  fulfillment_type: fulfillment, digital_type: 'file', digital_config: {},
});

const ASSET_CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB per potongan
const ASSET_MAX_BYTES = 25 * 1024 * 1024;
const randomUploadId = () =>
  (Math.random().toString(36).slice(2) + Date.now().toString(36) + Math.random().toString(36).slice(2)).slice(0, 40);

const Step3Products = ({ data, setData, onNext, onBack, onSkip }) => {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const businessType = data.business_type || 'physical';
  const isBothMode = businessType === 'both';
  const defaultFulfillment = businessType === 'digital' ? 'digital' : 'physical';
  const [mode, setMode] = useState(null);
  const [products, setProducts] = useState(data.products || []);
  const [newProduct, setNewProduct] = useState(emptyProduct(defaultFulfillment));
  const [editingId, setEditingId] = useState(null);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [enableVariants, setEnableVariants] = useState(false);
  const [newVariant, setNewVariant] = useState({ name: '', price: '', stock: '' });
  const isDigital = (newProduct.fulfillment_type || defaultFulfillment) === 'digital';
  const digitalType = newProduct.digital_type || 'file';
  const digitalConfig = newProduct.digital_config || {};

  // Aset digital (file & stok kode) supaya seller tidak perlu menyusul di dashboard.
  const [assets, setAssets] = useState([]);
  const [pools, setPools] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [poolForm, setPoolForm] = useState({ open: false, name: '', codes: '', saving: false });
  const assetInputRef = useRef(null);

  const fetchDigitalResources = async () => {
    try {
      const [a, p] = await Promise.all([
        axios.get(`${API}/digital/assets`, { headers: authHeaders }),
        axios.get(`${API}/digital/code-pools`, { headers: authHeaders }),
      ]);
      setAssets(a.data.assets || []);
      setPools(p.data.pools || []);
    } catch (e) {
      // Tidak fatal: seller masih bisa melampirkan file nanti dari menu Produk Digital.
    }
  };

  useEffect(() => {
    if (isDigital) fetchDigitalResources();
  }, [isDigital]);

  const setConfig = (patch) =>
    setNewProduct((prev) => ({ ...prev, digital_config: { ...(prev.digital_config || {}), ...patch } }));

  const changeDigitalType = (value) => {
    setNewProduct({ ...newProduct, digital_type: value });
  };

  const handleAssetUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > ASSET_MAX_BYTES) {
      toast.error('Ukuran file maksimal 25 MB');
      return;
    }
    setUploadingAsset(true);
    setUploadPct(0);
    const uploadId = randomUploadId();
    const total = Math.ceil(file.size / ASSET_CHUNK_SIZE);
    try {
      for (let i = 0; i < total; i++) {
        const form = new FormData();
        form.append('upload_id', uploadId);
        form.append('chunk_index', i);
        form.append('total_chunks', total);
        form.append('chunk', file.slice(i * ASSET_CHUNK_SIZE, (i + 1) * ASSET_CHUNK_SIZE));
        await axios.post(`${API}/digital/assets/upload-chunk`, form, { headers: authHeaders });
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      const res = await axios.post(`${API}/digital/assets/upload-complete`,
        { upload_id: uploadId, file_name: file.name }, { headers: authHeaders });
      // Tambah ke daftar + pilih dalam satu render supaya <select> tidak sempat kosong.
      setAssets((prev) => [res.data.asset, ...prev.filter((a) => a.id !== res.data.asset.id)]);
      setConfig({ asset_id: res.data.asset.id });
      toast.success('File terupload & langsung dipilih');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload gagal, coba lagi');
    } finally {
      setUploadingAsset(false);
      setUploadPct(0);
    }
  };

  const handleCreatePool = async () => {
    if (!poolForm.name.trim()) {
      toast.error('Nama stok kode wajib diisi');
      return;
    }
    setPoolForm((p) => ({ ...p, saving: true }));
    try {
      const res = await axios.post(`${API}/digital/code-pools`,
        { name: poolForm.name, codes: poolForm.codes }, { headers: authHeaders });
      const pool = { ...res.data.pool, available: res.data.added, used: 0, total: res.data.added };
      setPools((prev) => [pool, ...prev.filter((p) => p.id !== pool.id)]);
      setConfig({ code_pool_id: pool.id });
      toast.success(`Stok kode dibuat (${res.data.added} kode)`);
      setPoolForm({ open: false, name: '', codes: '', saving: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat stok kode');
      setPoolForm((p) => ({ ...p, saving: false }));
    }
  };

  // Kalau mode toko diubah di langkah 1, produk yang sudah diinput ikut disesuaikan.
  useEffect(() => {
    if (isBothMode) return;
    if (!products.some(p => (p.fulfillment_type || 'physical') !== defaultFulfillment)) return;
    const fixed = products.map(p => ({
      ...p,
      fulfillment_type: defaultFulfillment,
      digital_type: defaultFulfillment === 'digital' ? (p.digital_type || 'file') : null,
    }));
    setProducts(fixed);
    setData({ ...data, products: fixed });
    setNewProduct(prev => ({ ...prev, fulfillment_type: defaultFulfillment }));
  }, [businessType]);
  
  // Import states
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const addVariantToProduct = () => {
    if (!newVariant.name) return;
    const variant = {
      name: newVariant.name,
      price: parseFloat(newVariant.price) || 0,
      stock: parseInt(newVariant.stock) || 0
    };
    const variants = editingVariantIndex != null
      ? newProduct.variants.map((v, i) => (i === editingVariantIndex ? variant : v))
      : [...newProduct.variants, variant];
    setNewProduct({ ...newProduct, variants });
    setNewVariant({ name: '', price: '', stock: '' });
    setEditingVariantIndex(null);
  };

  const editVariant = (index) => {
    const v = newProduct.variants[index];
    setNewVariant({ name: v.name || '', price: v.price ?? '', stock: v.stock ?? '' });
    setEditingVariantIndex(index);
  };

  const removeVariantFromProduct = (index) => {
    const updated = newProduct.variants.filter((_, i) => i !== index);
    setNewProduct({ ...newProduct, variants: updated });
    setEditingVariantIndex(null);
    setNewVariant({ name: '', price: '', stock: '' });
    if (updated.length === 0) setEnableVariants(false);
  };

  const toggleVariants = (enabled) => {
    setEnableVariants(enabled);
    if (!enabled) {
      setNewProduct({ ...newProduct, variants: [] });
    }
  };

  const formatPrice = (price) => {
    let numericPrice = price;
    if (typeof price === 'string') {
      numericPrice = parseFloat(price.replace(/[Rp\s.]/g, '').replace(',', '.')) || 0;
    }
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(numericPrice);
  };

  const resetForm = () => {
    setNewProduct(emptyProduct(defaultFulfillment));
    setEnableVariants(false);
    setNewVariant({ name: '', price: '', stock: '' });
    setEditingId(null);
    setEditingVariantIndex(null);
  };

  const saveProduct = () => {
    const hasVariants = enableVariants && newProduct.variants.length > 0;
    if (!newProduct.name || (!hasVariants && !newProduct.price)) {
      toast.error(hasVariants ? 'Nama produk wajib diisi' : 'Nama dan harga wajib diisi');
      return;
    }
    const kind = isBothMode ? (newProduct.fulfillment_type || 'physical') : defaultFulfillment;
    const dtype = newProduct.digital_type || 'file';
    // Simpan hanya konfigurasi yang relevan dengan cara kirim yang dipilih.
    const rawCfg = newProduct.digital_config || {};
    const keepKey = { file: 'asset_id', license_code: 'code_pool_id', link_access: 'access_url' }[dtype];
    const cfg = rawCfg[keepKey] !== undefined && rawCfg[keepKey] !== '' ? { [keepKey]: rawCfg[keepKey] } : {};
    if (kind === 'digital' && dtype === 'file') {
      cfg.download_limit = rawCfg.download_limit ?? 5;
      cfg.link_ttl_hours = rawCfg.link_ttl_hours ?? 168;
    }
    const productData = {
      ...newProduct,
      id: editingId ?? Date.now(),
      fulfillment_type: kind,
      digital_type: kind === 'digital' ? (newProduct.digital_type || 'file') : null,
      digital_config: kind === 'digital' ? cfg : {},
      price: hasVariants ? null : newProduct.price,
      stock: hasVariants ? null : (newProduct.stock || null),
      variants: hasVariants ? newProduct.variants : []
    };
    if (kind === 'digital') {
      if (dtype === 'file' && !cfg.asset_id) toast.warning('Produk disimpan, tapi filenya belum dipilih — lampirkan nanti di menu Produk Digital');
      if (dtype === 'license_code' && !cfg.code_pool_id) toast.warning('Produk disimpan, tapi stok kodenya belum dipilih — atur nanti di menu Produk Digital');
    }
    const updated = editingId != null
      ? products.map((p) => (p.id === editingId ? productData : p))
      : [...products, productData];
    setProducts(updated);
    setData({ ...data, products: updated });
    resetForm();
    toast.success(editingId != null ? 'Produk diperbarui!' : 'Produk ditambahkan!');
  };

  const editProduct = (p) => {
    setEditingId(p.id);
    setNewProduct({
      name: p.name || '',
      price: p.price ?? '',
      description: p.description || '',
      stock: p.stock ?? '',
      variants: p.variants || [],
      image_url: p.image_url || '',
      fulfillment_type: p.fulfillment_type || defaultFulfillment,
      digital_type: p.digital_type || 'file',
      digital_config: p.digital_config || {},
    });
    setEnableVariants((p.variants || []).length > 0);
    setMode('manual');
  };

  const removeProduct = (id) => {
    const updated = products.filter(p => p.id !== id);
    setProducts(updated);
    setData({ ...data, products: updated });
    if (editingId === id) resetForm();
  };

  // Tanpa produk, tombol utama berfungsi sebagai "Lewati" (tidak ada tombol Lewati terpisah).
  const handleNext = () => {
    if (products.length === 0) {
      onSkip();
      return;
    }
    setData({ ...data, products });
    onNext();
  };

  // Handle file selection for import
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setSelectedFile(file);
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Get user_id from token
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      const response = await axios.post(
        `${API}/products/import/preview?user_id=${payload.user_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        setImportPreview(response.data);
        toast.success(`Ditemukan ${response.data.total_rows} produk`);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Gagal membaca file');
      setSelectedFile(null);
    } finally {
      setImporting(false);
    }
  };

  // Confirm import
  const handleConfirmImport = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      const response = await axios.post(
        `${API}/products/import/excel?user_id=${payload.user_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Update local products list with imported data
        const importedProducts = response.data.products.map((p, idx) => ({
          ...p,
          id: `imported-${idx}`
        }));
        setProducts([...products, ...importedProducts]);
        setData({ ...data, products: [...products, ...importedProducts] });
        setImportPreview(null);
        setSelectedFile(null);
        setMode(null);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Gagal import produk');
    } finally {
      setImporting(false);
    }
  };

  if (!mode) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 dark:bg-orange-900/30 rounded-xl sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Package className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600 dark:text-orange-400" />
          </div>
          <h2 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">Tambah Katalog Produk</h2>
          <p className="text-sm sm:text-base text-slate-600 dark:text-zinc-400 mt-2">AI akan menggunakan ini untuk menjawab pertanyaan customer</p>
        </div>

        <div className="space-y-2 sm:space-y-3">
          <div
            className="border-2 border-slate-200 dark:border-zinc-800 hover:border-orange-500 rounded-lg sm:rounded-xl p-3 sm:p-4 cursor-pointer transition-all"
            onClick={() => setMode('manual')}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center shrink-0">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50"><FileEdit className="w-4 h-4 inline mr-1" />Tambah Manual</p>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Input produk satu per satu</p>
              </div>
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500 shrink-0 dark:text-zinc-400" />
            </div>
          </div>

          {/* Import dari Excel/CSV - Hidden (under development) */}
          {false && (
          <div
            className="border-2 border-slate-200 dark:border-zinc-800 hover:border-orange-500 rounded-lg sm:rounded-xl p-3 sm:p-4 cursor-pointer transition-all"
            onClick={() => setMode('import')}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center shrink-0">
                <FileSpreadsheet className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50"><BarChart3 className="w-4 h-4 inline mr-1" />Import dari Excel/CSV</p>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Upload file, AI mapping kolom otomatis</p>
              </div>
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500 shrink-0 dark:text-zinc-400" />
            </div>
          </div>
          )}

          <div className="border-2 border-slate-200 dark:border-zinc-800 rounded-lg sm:rounded-xl p-3 sm:p-4 opacity-60">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center shrink-0">
                <Store className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50"><ShoppingCart className="w-4 h-4 inline mr-1" />Sync dari Marketplace</p>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Tokopedia, Shopee, TikTok Shop</p>
              </div>
              <span className="text-[10px] sm:text-xs bg-slate-200 dark:bg-zinc-800 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded shrink-0">Coming Soon</span>
            </div>
          </div>
        </div>

        <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-2.5 sm:p-3">
          <p className="text-xs sm:text-sm text-blue-800 dark:text-indigo-200">
            <Lightbulb className="w-4 h-4 inline mr-1 text-indigo-600 dark:text-indigo-400" />Belum punya katalog? Tidak apa-apa! AI tetap bisa menjawab pertanyaan umum.
          </p>
        </div>

        <div className="flex gap-2 sm:gap-3 flex-wrap">
          <Button variant="outline" onClick={onBack} className="gap-1 sm:gap-2 text-sm px-3 sm:px-4">
            <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Kembali</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <Button onClick={handleNext} className="flex-1 gap-1 sm:gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="onboarding-next-btn">
            {products.length === 0 ? 'Lewati' : 'Lanjut'}
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Mode: Import from Excel/CSV
  if (mode === 'import') {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50"><FileSpreadsheet className="w-6 h-6 inline mr-2" />Import dari Excel/CSV</h2>
          <p className="text-slate-500 mt-2 dark:text-zinc-400">AI otomatis mengenali kolom seperti "Jumlah Barang" = Stok</p>
        </div>

        {!importPreview ? (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-300 dark:border-zinc-700 rounded-xl p-8 text-center hover:border-green-500 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="excel-upload"
                disabled={importing}
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                {importing ? (
                  <Loader2 className="w-12 h-12 mx-auto text-green-500 animate-spin mb-3" />
                ) : (
                  <Upload className="w-12 h-12 mx-auto text-slate-500 mb-3 dark:text-zinc-400" />
                )}
                <p className="font-medium text-slate-700 dark:text-zinc-300">
                  {importing ? 'Membaca file...' : 'Klik untuk upload file'}
                </p>
                <p className="text-sm text-slate-500 mt-1 dark:text-zinc-400">Excel (.xlsx, .xls) atau CSV</p>
              </label>
            </div>

            <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 text-sm">
              <p className="font-medium text-blue-800 dark:text-indigo-300 mb-2"><Lightbulb className="w-4 h-4 inline mr-1" />Tips Format Excel:</p>
              <ul className="text-indigo-700 dark:text-indigo-400 space-y-1 list-disc list-inside">
                <li>Baris pertama = nama kolom</li>
                <li>AI mengerti berbagai nama: "Harga", "Price", "Harga Jual"</li>
                <li>Stok bisa ditulis: "Stok", "Stock", "Jumlah", "Qty"</li>
                <li>Berat bisa ditulis: "Berat", "Weight", "Gram"</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Column Mapping Preview */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="font-medium text-green-800 dark:text-green-300 mb-2"><CheckCircle className="w-4 h-4 inline mr-1" />Kolom Terdeteksi:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(importPreview.column_mapping).map(([field, column]) => (
                  <span key={field} className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-1 rounded text-sm">
                    {column} → <strong>{field}</strong>
                  </span>
                ))}
              </div>
            </div>

            {/* Sample Data */}
            <div className="border border-slate-200 dark:border-zinc-800 rounded-lg overflow-hidden">
              <p className="bg-slate-100 dark:bg-zinc-900 px-4 py-2 font-medium text-sm">
                Preview ({importPreview.total_rows} produk)
              </p>
              <div className="max-h-48 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-zinc-900">
                    <tr>
                      <th className="px-3 py-2 text-left">Nama</th>
                      <th className="px-3 py-2 text-left">Harga</th>
                      <th className="px-3 py-2 text-left">Stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.sample_data.slice(0, 5).map((row, idx) => (
                      <tr key={idx} className="border-t border-slate-100 dark:border-zinc-800">
                        <td className="px-3 py-2">{row[importPreview.column_mapping.name] || '-'}</td>
                        <td className="px-3 py-2">{row[importPreview.column_mapping.price] || '-'}</td>
                        <td className="px-3 py-2">{row[importPreview.column_mapping.stock] || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setImportPreview(null);
                  setSelectedFile(null);
                }}
                className="flex-1"
              >
                Ganti File
              </Button>
              <Button 
                onClick={handleConfirmImport}
                disabled={importing}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Import {importPreview.total_rows} Produk
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-2 sm:gap-3 flex-wrap">
          <Button variant="outline" onClick={() => setMode(null)} className="gap-1 sm:gap-2 text-sm px-3 sm:px-4">
            <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Kembali</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <Button onClick={handleNext} className="flex-1 gap-1 sm:gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="onboarding-next-btn">
            {products.length === 0 ? 'Lewati' : 'Lanjut'}
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" data-testid="onboarding-product-form-title">
          {businessType === 'digital' ? 'Tambah Produk Digital' : isBothMode ? 'Tambah Produk' : 'Tambah Produk Fisik'}
        </h2>
        {businessType === 'digital' && (
          <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1" data-testid="onboarding-digital-mode-note">
            Semua produk di toko ini disimpan sebagai produk digital — tanpa ongkir & berat.
          </p>
        )}
        {isBothMode && (
          <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1" data-testid="onboarding-both-mode-note">
            Pilih jenis tiap produk: fisik (dikirim kurir) atau digital (dikirim ke email).
          </p>
        )}
      </div>

      <div className="border border-slate-200 dark:border-zinc-800 rounded-xl p-4 space-y-3">
        <div>
          <Label>Nama Produk <span className="text-red-500">*</span></Label>
          <Input
            value={newProduct.name}
            onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
            placeholder="Contoh: Buket Mawar Merah"
            data-testid="onboarding-product-name"
          />
        </div>
        {isBothMode && (
          <div className="grid grid-cols-2 gap-2" data-testid="onboarding-fulfillment-toggle">
            {[
              { value: 'physical', label: 'Produk Fisik', note: 'Dikirim kurir' },
              { value: 'digital', label: 'Produk Digital', note: 'Dikirim ke email' },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setNewProduct({ ...newProduct, fulfillment_type: opt.value })}
                data-testid={`onboarding-fulfillment-${opt.value}`}
                className={`text-left rounded-xl border p-3 transition-colors ${(newProduct.fulfillment_type || 'physical') === opt.value
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 dark:border-indigo-500'
                  : 'border-slate-200 hover:border-slate-300 dark:border-zinc-800 dark:hover:border-zinc-700'}`}
              >
                <p className="text-sm font-medium text-slate-800 dark:text-zinc-200">{opt.label}</p>
                <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">{opt.note}</p>
              </button>
            ))}
          </div>
        )}

        {isDigital && (
          <div className="rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50 p-3 space-y-2" data-testid="onboarding-digital-config">
            <Label className="text-sm">Cara kirim ke pembeli</Label>
            <div className="grid grid-cols-3 gap-2">
              {DIGITAL_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => changeDigitalType(opt.value)}
                  data-testid={`onboarding-digital-type-${opt.value}`}
                  className={`px-2 py-2 rounded-lg border text-xs font-medium transition-colors ${digitalType === opt.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 dark:border-zinc-800 dark:text-zinc-300'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {digitalType === 'link_access' && (
              <Input
                value={digitalConfig.access_url || ''}
                onChange={(e) => setConfig({ access_url: e.target.value })}
                placeholder="https://t.me/+kodegrup"
                data-testid="onboarding-access-url"
              />
            )}

            {digitalType === 'file' && (
              <div className="space-y-2">
                <select
                  value={digitalConfig.asset_id || ''}
                  onChange={(e) => setConfig({ asset_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 text-sm text-slate-900 dark:text-zinc-50"
                  data-testid="onboarding-asset-select"
                >
                  <option value="">-- Pilih file yang dikirim ke pembeli --</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>{a.file_name}</option>
                  ))}
                </select>
                <input type="file" ref={assetInputRef} className="hidden" onChange={handleAssetUpload} data-testid="onboarding-asset-file-input" />
                <Button
                  type="button" variant="outline" size="sm" className="w-full gap-2"
                  disabled={uploadingAsset}
                  onClick={() => assetInputRef.current?.click()}
                  data-testid="onboarding-upload-asset-btn"
                >
                  {uploadingAsset
                    ? <><Loader2 className="w-4 h-4 animate-spin" />Mengupload {uploadPct}%</>
                    : <><UploadCloud className="w-4 h-4" />Upload File Sekarang</>}
                </Button>
                <p className="text-xs text-slate-500 dark:text-zinc-400">
                  Maks 25 MB (PDF, ZIP, gambar, dll). Bisa juga dilampirkan nanti di menu Produk Digital.
                </p>
              </div>
            )}

            {digitalType === 'license_code' && (
              <div className="space-y-2">
                <select
                  value={digitalConfig.code_pool_id || ''}
                  onChange={(e) => setConfig({ code_pool_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 text-sm text-slate-900 dark:text-zinc-50"
                  data-testid="onboarding-code-pool-select"
                >
                  <option value="">-- Pilih stok kode --</option>
                  {pools.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} (sisa {p.available ?? 0})</option>
                  ))}
                </select>
                {!poolForm.open ? (
                  <Button type="button" variant="outline" size="sm" className="w-full gap-2"
                    onClick={() => setPoolForm({ ...poolForm, open: true })}
                    data-testid="onboarding-new-pool-btn">
                    <Plus className="w-4 h-4" />Buat Stok Kode Sekarang
                  </Button>
                ) : (
                  <div className="space-y-2 rounded-lg border border-slate-200 dark:border-zinc-800 p-2 bg-white dark:bg-zinc-900">
                    <Input
                      value={poolForm.name}
                      onChange={(e) => setPoolForm({ ...poolForm, name: e.target.value })}
                      placeholder="Nama stok (mis. Lisensi Canva Pro)"
                      data-testid="onboarding-pool-name"
                    />
                    <Textarea
                      value={poolForm.codes}
                      onChange={(e) => setPoolForm({ ...poolForm, codes: e.target.value })}
                      placeholder={'Tempel kode di sini, satu kode per baris\nABC-123\nDEF-456'}
                      rows={3}
                      data-testid="onboarding-pool-codes"
                    />
                    <div className="flex gap-2">
                      <Button type="button" variant="ghost" size="sm"
                        onClick={() => setPoolForm({ open: false, name: '', codes: '', saving: false })}
                        data-testid="onboarding-cancel-pool-btn">
                        Batal
                      </Button>
                      <Button type="button" size="sm" className="flex-1 gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                        disabled={poolForm.saving}
                        onClick={handleCreatePool}
                        data-testid="onboarding-save-pool-btn">
                        {poolForm.saving ? <><Loader2 className="w-4 h-4 animate-spin" />Menyimpan...</> : <><Check className="w-4 h-4" />Simpan Stok Kode</>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Variant Toggle — sebelum harga & stok, supaya seller tidak isi harga dua kali */}
        <label className="flex items-center gap-3 cursor-pointer select-none pt-1" data-testid="onboarding-variant-toggle" onClick={() => toggleVariants(!enableVariants)}>
          <div className={`relative w-11 h-6 rounded-full transition-colors ${enableVariants ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-zinc-700'}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enableVariants ? 'translate-x-5' : 'translate-x-0'} dark:bg-zinc-900`} />
          </div>
          <span className="text-sm font-medium text-slate-700 dark:text-zinc-300 flex items-center gap-2">
            <Package className="w-4 h-4" />
            Produk memiliki varian
          </span>
        </label>

        {!enableVariants && (
          <>
            <div>
              <Label>Harga <span className="text-red-500">*</span></Label>
              <Input
                value={newProduct.price}
                onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                placeholder="Contoh: 150000"
                data-testid="onboarding-product-price"
              />
            </div>
            <div>
              <Label>Stok</Label>
              <Input
                type="number"
                value={newProduct.stock}
                onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                placeholder="Jumlah stok (opsional)"
                data-testid="onboarding-product-stock"
              />
            </div>
          </>
        )}

        {/* Variant Section */}
        {enableVariants && (
          <div className="border border-slate-200 dark:border-zinc-800 rounded-xl p-3 space-y-2 bg-slate-50 dark:bg-zinc-900/50">
            <p className="text-xs text-slate-500 dark:text-zinc-400">
              Setiap varian memiliki harga dan stok masing-masing
            </p>
            
            {newProduct.variants.map((variant, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 bg-white dark:bg-zinc-900 rounded-lg p-2 border ${editingVariantIndex === index ? 'border-indigo-500' : 'border-slate-200 dark:border-zinc-800'}`}
                data-testid={`onboarding-variant-item-${index}`}
              >
                <span className="flex-1 text-sm font-medium text-slate-700 dark:text-zinc-300 truncate">{variant.name}</span>
                <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 dark:bg-blue-900/50 px-2 py-0.5 rounded dark:text-indigo-400">
                  {formatPrice(variant.price || 0)}
                </span>
                <span className="text-xs text-green-700 bg-green-50 dark:bg-green-900/50 px-2 py-0.5 rounded dark:text-green-300">
                  Stok: {variant.stock ?? 0}
                </span>
                <button
                  onClick={() => editVariant(index)}
                  className="text-slate-500 hover:text-indigo-600 dark:text-zinc-400 dark:hover:text-indigo-400 p-1 rounded"
                  type="button"
                  title="Edit varian"
                  data-testid={`onboarding-edit-variant-${index}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  onClick={() => removeVariantFromProduct(index)}
                  className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 p-1 rounded"
                  type="button"
                  title="Hapus varian"
                  data-testid={`onboarding-remove-variant-${index}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            
            <div className="flex gap-2">
              <Input
                value={newVariant.name}
                onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                placeholder="Nama (M, L, Merah...)"
                className="flex-1"
                data-testid="onboarding-variant-name"
              />
              <Input
                type="number"
                value={newVariant.price}
                onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value })}
                placeholder="Harga"
                className="w-24"
                data-testid="onboarding-variant-price"
              />
              <Input
                type="number"
                value={newVariant.stock}
                onChange={(e) => setNewVariant({ ...newVariant, stock: e.target.value })}
                placeholder="Stok"
                className="w-20"
                data-testid="onboarding-variant-stock"
              />
            </div>
            <div className="flex gap-2">
              {editingVariantIndex != null && (
                <Button variant="ghost" size="sm" type="button" onClick={() => { setEditingVariantIndex(null); setNewVariant({ name: '', price: '', stock: '' }); }} data-testid="onboarding-cancel-variant-btn">
                  Batal
                </Button>
              )}
              <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={addVariantToProduct} disabled={!newVariant.name} type="button" data-testid="onboarding-add-variant-btn">
                {editingVariantIndex != null ? <><Check className="w-4 h-4" />Simpan Varian</> : <><Plus className="w-4 h-4" />Tambah Varian</>}
              </Button>
            </div>
          </div>
        )}

        <div>
          <Label>Deskripsi</Label>
          <Textarea
            value={newProduct.description}
            onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
            placeholder="Deskripsi singkat produk..."
            rows={2}
          />
        </div>

        <div data-testid="onboarding-product-image">
          <Label>Foto Produk</Label>
          <p className="text-xs text-slate-500 dark:text-zinc-400 mb-2">
            AI bisa langsung mengirim foto ini saat customer minta lihat produk.
          </p>
          <ImageUpload
            value={newProduct.image_url}
            onChange={(url) => setNewProduct((prev) => ({ ...prev, image_url: url }))}
          />
        </div>

        <div className="flex gap-2">
          {editingId != null && (
            <Button onClick={resetForm} variant="ghost" className="gap-2" data-testid="onboarding-cancel-edit-btn">
              <X className="w-4 h-4" />
              Batal
            </Button>
          )}
          <Button onClick={saveProduct} className="flex-1 gap-2 bg-slate-900 hover:bg-slate-800 text-white dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white" data-testid="onboarding-save-product-btn">
            {editingId != null ? <><Check className="w-4 h-4" />Simpan Perubahan Produk</> : <><Package className="w-4 h-4" />Simpan Produk Ini</>}
          </Button>
        </div>
        <p className="text-xs text-slate-500 dark:text-zinc-400 text-center">
          {editingId != null
            ? 'Perubahan langsung tersimpan ke daftar produk di bawah.'
            : 'Produk masuk ke daftar di bawah dan form dikosongkan untuk produk berikutnya.'}
        </p>
      </div>

      {products.length > 0 && (
        <div>
          <p className="text-sm text-slate-600 dark:text-zinc-400 mb-2">Produk ditambahkan ({products.length}):</p>
          <div className="grid grid-cols-2 gap-2">
            {products.map((p) => (
              <div
                key={p.id}
                className={`bg-slate-50 dark:bg-zinc-900 rounded-lg p-3 relative border ${editingId === p.id ? 'border-indigo-500' : 'border-transparent'}`}
                data-testid={`onboarding-product-item-${p.id}`}
              >
                <div className="absolute top-1 right-1 flex gap-0.5">
                  <button
                    onClick={() => editProduct(p)}
                    className="text-slate-500 hover:text-indigo-600 dark:text-zinc-400 dark:hover:text-indigo-400 p-0.5"
                    data-testid={`onboarding-edit-product-${p.id}`}
                    title="Edit produk"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => removeProduct(p.id)}
                    className="text-slate-500 hover:text-red-500 dark:text-zinc-400 p-0.5"
                    data-testid={`onboarding-remove-product-${p.id}`}
                    title="Hapus produk"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="font-medium text-sm truncate pr-12">{p.name}</p>
                {p.image_url && (
                  <img src={p.image_url} alt={p.name} className="w-full h-16 object-cover rounded mt-1" data-testid={`onboarding-product-thumb-${p.id}`} />
                )}
                {p.variants && p.variants.length > 0 ? (
                  <p className="text-xs text-slate-500 dark:text-zinc-400">{p.variants.length} varian</p>
                ) : (
                  <p className="text-xs text-slate-500 dark:text-zinc-400">{p.price}</p>
                )}
                {isBothMode && (
                  <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded ${p.fulfillment_type === 'digital'
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                    : 'bg-slate-200 text-slate-600 dark:bg-zinc-800 dark:text-zinc-300'}`}>
                    {p.fulfillment_type === 'digital' ? 'Digital' : 'Fisik'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 sm:gap-3 flex-wrap">
        <Button variant="outline" onClick={() => setMode(null)} className="gap-1 sm:gap-2 text-sm px-3 sm:px-4">
          <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Kembali</span>
          <span className="sm:hidden">Back</span>
        </Button>
        <Button onClick={handleNext} className="flex-1 gap-1 sm:gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="onboarding-next-btn">
          {products.length === 0 ? 'Lewati' : 'Selesai, Lanjut'}
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </div>
  );
};

// Step 4: Payment Setup
const Step4Payment = ({ data, setData, onNext, onBack, onSkip }) => {
  const [paymentMode, setPaymentMode] = useState(data.payment_mode || 'manual');
  const [availableMethods, setAvailableMethods] = useState({ banks: [], ewallets: [] });
  const [loadingMethods, setLoadingMethods] = useState(true);

  // Fetch available payment methods on mount
  useEffect(() => {
    const fetchMethods = async () => {
      try {
        const response = await axios.get(`${API}/payment/methods/available`);
        if (response.data.success) {
          setAvailableMethods({
            banks: response.data.banks || [],
            ewallets: response.data.ewallets || []
          });
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      } finally {
        setLoadingMethods(false);
      }
    };
    fetchMethods();
  }, []);

  // Tanpa satu pun input pembayaran, tombol utama berfungsi sebagai "Lewati"
  const hasPaymentInput = paymentMode !== 'manual'
    || !!(data.payment_method_key || data.bank_account || data.bank_holder);

  const handleNext = () => {
    if (!hasPaymentInput) {
      onSkip();
      return;
    }
    if (paymentMode === 'manual') {
      if (!data.payment_method_key) {
        toast.error('Pilih metode pembayaran terlebih dahulu');
        return;
      }
      if (!data.bank_account) {
        toast.error('Isi nomor rekening/HP terlebih dahulu');
        return;
      }
      if (!data.bank_holder) {
        toast.error('Isi nama pemilik terlebih dahulu');
        return;
      }
    }
    setData({ ...data, payment_mode: paymentMode });
    onNext();
  };

  // Get method name by key
  const getMethodName = (key) => {
    const bank = availableMethods.banks.find(b => b.key === key);
    if (bank) return bank.name;
    const wallet = availableMethods.ewallets.find(w => w.key === key);
    if (wallet) return wallet.name;
    return key;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 dark:bg-purple-900/30 rounded-xl sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <h2 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">Setup Pembayaran</h2>
        <p className="text-sm sm:text-base text-slate-600 dark:text-zinc-400 mt-2">Bagaimana customer akan membayar?</p>
      </div>

      <div className="space-y-3">
        {/* Transfer Manual Option */}
        <div
          className={`border-2 rounded-lg sm:rounded-xl p-3 sm:p-4 cursor-pointer transition-all ${
            paymentMode === 'manual'
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
              : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300'
          }`}
          onClick={() => setPaymentMode('manual')}
        >
          <div className="flex items-start gap-2 sm:gap-3">
            <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 ${
              paymentMode === 'manual' ? 'border-purple-500 bg-purple-500' : 'border-slate-300'
            }`}>
              {paymentMode === 'manual' && <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50"><Landmark className="w-4 h-4 inline mr-1" />Transfer Manual</p>
                <span className="text-[10px] sm:text-xs bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 rounded dark:text-green-300 dark:bg-green-950/40">Recommended</span>
              </div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-400 mt-1">
                Customer transfer ke rekening atau e-wallet Anda
              </p>
              {paymentMode === 'manual' && (
                <div className="mt-3 space-y-2 sm:space-y-3" onClick={(e) => e.stopPropagation()}>
                  {/* Bank/E-Wallet Selection */}
                  <div>
                    <Label className="text-xs sm:text-sm mb-1 block">Pilih Bank / E-Wallet</Label>
                    <Select 
                      value={data.payment_method_key || ''} 
                      onValueChange={(v) => setData({ ...data, payment_method_key: v })}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Pilih metode pembayaran" />
                      </SelectTrigger>
                      <SelectContent>
                        {loadingMethods ? (
                          <div className="p-2 text-sm text-center text-slate-500 dark:text-zinc-400">Loading...</div>
                        ) : (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-zinc-400">Bank Transfer</div>
                            {availableMethods.banks.map((bank) => (
                              <SelectItem key={bank.key} value={bank.key}>
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4" />
                                  {bank.name}
                                </div>
                              </SelectItem>
                            ))}
                            <div className="px-2 py-1 text-xs font-semibold text-slate-500 mt-2 dark:text-zinc-400">E-Wallet</div>
                            {availableMethods.ewallets.map((ew) => (
                              <SelectItem key={ew.key} value={ew.key}>
                                <div className="flex items-center gap-2">
                                  <Wallet className="w-4 h-4" />
                                  {ew.name}
                                </div>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Account Number */}
                  {data.payment_method_key && (
                    <>
                      <div>
                        <Label className="text-xs sm:text-sm mb-1 block">
                          {availableMethods.banks.some(b => b.key === data.payment_method_key) ? 'Nomor Rekening' : 'Nomor HP'}
                        </Label>
                        <Input
                          value={data.bank_account || ''}
                          onChange={(e) => setData({ ...data, bank_account: e.target.value })}
                          placeholder={availableMethods.banks.some(b => b.key === data.payment_method_key) ? "1234567890" : "081234567890"}
                          className="text-sm"
                        />
                      </div>

                      <div>
                        <Label className="text-xs sm:text-sm mb-1 block">Nama Pemilik</Label>
                        <Input
                          value={data.bank_holder || ''}
                          onChange={(e) => setData({ ...data, bank_holder: e.target.value })}
                          placeholder="Nama sesuai rekening/akun"
                          className="text-sm"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Gateway Option - HIDDEN: masih dalam pengembangan
        <div
          className={`border-2 rounded-lg sm:rounded-xl p-3 sm:p-4 cursor-pointer transition-all ${
            paymentMode === 'gateway'
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
              : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300'
          }`}
          onClick={() => setPaymentMode('gateway')}
        >
          <div className="flex items-start gap-2 sm:gap-3">
            <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 ${
              paymentMode === 'gateway' ? 'border-purple-500 bg-purple-500' : 'border-slate-300'
            }`}>
              {paymentMode === 'gateway' && <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50">Payment Gateway (Otomatis)</p>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-400 mt-1">
                VA, QRIS, E-Wallet - Status otomatis update
              </p>
              <p className="text-[10px] sm:text-xs text-slate-500 mt-1 dark:text-zinc-400">Fee: 1.5% - 3% per transaksi</p>
            </div>
          </div>
        </div>
        */}
      </div>

      <div className="flex gap-2 sm:gap-3 flex-wrap">
        <Button variant="outline" onClick={onBack} className="gap-1 sm:gap-2 text-sm px-3 sm:px-4">
          <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Kembali</span>
          <span className="sm:hidden">Back</span>
        </Button>
        <Button onClick={handleNext} className="flex-1 gap-1 sm:gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="onboarding-next-btn">
          {hasPaymentInput ? 'Lanjut' : 'Lewati'}
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </div>
  );
};

// Step 5: AI Customization
const Step5AI = ({ data, setData, onNext, onBack, onSkip }) => {
  const [agentType, setAgentType] = useState(data.agent_type || 'sales');
  const [aiStyle, setAiStyle] = useState(data.ai_style || 'friendly');
  const [welcomeMsg, setWelcomeMsg] = useState(
    data.welcome_message || `Halo! Selamat datang di ${data.store_name || 'Toko Kami'}.\nAda yang bisa saya bantu?`
  );
  const [additionalInfo, setAdditionalInfo] = useState(data.ai_additional_info || '');
  const [marketplaceLinks, setMarketplaceLinks] = useState(data.marketplace_links || []);
  const businessType = data.business_type || 'physical';

  // Fitur Sales Agent mengikuti mode toko: toko digital tidak menghitung ongkir
  const salesFeatures = businessType === 'digital'
    ? ['Rekap pesanan otomatis', 'Kirim file/kode otomatis', 'Proses pembayaran', 'Generate invoice']
    : businessType === 'both'
      ? ['Rekap pesanan otomatis', 'Hitung ongkir (produk fisik)', 'Kirim file otomatis (produk digital)', 'Generate invoice']
      : ['Rekap pesanan otomatis', 'Hitung ongkir', 'Proses pembayaran', 'Generate invoice'];

  const AGENT_TYPES = [
    {
      id: 'sales',
      icon: ShoppingCart,
      label: 'Sales Agent',
      description: 'AI yang handle customer dari awal hingga closing. Cocok untuk toko yang jualan langsung via WhatsApp, Instagram, atau Facebook.',
      features: salesFeatures
    },
    {
      id: 'cs',
      icon: Headphones,
      label: 'CS Agent',
      description: 'AI customer service untuk menjawab pertanyaan customer. Cocok untuk seller marketplace (Shopee, Tokopedia, TikTok Shop) yang butuh CS otomatis.',
      features: ['Jawab pertanyaan produk', 'Info toko & FAQ', 'Arahkan ke marketplace', 'Tanpa flow order']
    }
  ];

  const MARKETPLACE_OPTIONS = ['Shopee', 'Tokopedia', 'TikTok Shop', 'Lazada', 'Bukalapak', 'Lainnya'];

  // AI Styles options
  const AI_STYLES = [
    { id: 'friendly', label: 'Ramah & Santai', example: 'Halo kak! Ada yang bisa dibantu hari ini?' },
    { id: 'professional', label: 'Profesional', example: 'Selamat datang. Ada yang bisa saya bantu?' },
    { id: 'casual', label: 'Gaul & Kekinian', example: 'Hai! Mau cari apa nih?' }
  ];

  const addMarketplaceLink = () => {
    setMarketplaceLinks([...marketplaceLinks, { platform: 'Shopee', url: '' }]);
  };

  const removeMarketplaceLink = (index) => {
    setMarketplaceLinks(marketplaceLinks.filter((_, i) => i !== index));
  };

  const updateMarketplaceLink = (index, field, value) => {
    const updated = [...marketplaceLinks];
    updated[index] = { ...updated[index], [field]: value };
    setMarketplaceLinks(updated);
  };

  const handleNext = () => {
    setData({
      ...data,
      agent_type: agentType,
      ai_style: aiStyle,
      welcome_message: welcomeMsg,
      ai_additional_info: additionalInfo,
      marketplace_links: agentType === 'cs' ? marketplaceLinks.filter(l => l.url) : []
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Bot className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">Kustomisasi AI</h2>
        <p className="text-slate-600 dark:text-zinc-400 mt-2">Atur tipe dan cara AI melayani customer Anda</p>
      </div>

      <div className="space-y-5">
        {/* Agent Type Selection */}
        <div>
          <Label className="mb-3 block font-semibold">Tipe AI Agent</Label>
          <div className="space-y-3">
            {AGENT_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <div
                  key={type.id}
                  data-testid={`agent-type-${type.id}`}
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    agentType === type.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300'
                  }`}
                  onClick={() => setAgentType(type.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      agentType === type.id ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                    }`}>
                      {agentType === type.id && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-4 h-4 ${agentType === type.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-zinc-400'} dark:text-indigo-400`} />
                        <p className="font-semibold text-slate-900 dark:text-zinc-50">{type.label}</p>
                      </div>
                      <p className="text-sm text-slate-500 dark:text-zinc-400 mb-2">{type.description}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {type.features.map((f, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Marketplace Links - Only for CS Agent */}
        {agentType === 'cs' && (
          <div data-testid="marketplace-links-section">
            <Label className="mb-2 block font-semibold">Link Marketplace</Label>
            <p className="text-sm text-slate-500 mb-3 dark:text-zinc-400">Customer akan diarahkan ke link ini saat mau beli</p>
            {marketplaceLinks.map((link, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Select value={link.platform} onValueChange={(val) => updateMarketplaceLink(index, 'platform', val)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MARKETPLACE_OPTIONS.map(mp => (
                      <SelectItem key={mp} value={mp}>{mp}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={link.url}
                  onChange={(e) => updateMarketplaceLink(index, 'url', e.target.value)}
                  placeholder="https://shopee.co.id/toko-anda"
                  className="flex-1"
                />
                <Button variant="ghost" size="icon" onClick={() => removeMarketplaceLink(index)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addMarketplaceLink} className="gap-1">
              <Plus className="w-3 h-3" /> Tambah Link
            </Button>
          </div>
        )}

        {/* AI Style */}
        <div>
          <Label className="mb-3 block font-semibold">Gaya Bahasa AI</Label>
          <div className="space-y-2">
            {AI_STYLES.map((style) => (
              <div
                key={style.id}
                className={`border-2 rounded-xl p-3 cursor-pointer transition-all ${
                  aiStyle === style.id
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                    : 'border-slate-200 dark:border-zinc-800'
                }`}
                onClick={() => setAiStyle(style.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    aiStyle === style.id ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                  }`}>
                    {aiStyle === style.id && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-zinc-50">{style.label}</p>
                    <p className="text-sm text-slate-500 dark:text-zinc-400">"{style.example}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label>Pesan Pembuka</Label>
          <Textarea
            value={welcomeMsg}
            onChange={(e) => setWelcomeMsg(e.target.value)}
            rows={3}
            className="mt-1"
          />
        </div>

        <div>
          <Label>Info Tambahan untuk AI (opsional)</Label>
          <Textarea
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            placeholder="Contoh: jam operasional, area pengiriman, promo, dll"
            rows={3}
            className="mt-1"
          />
        </div>
      </div>

      <div className="flex gap-2 sm:gap-3 flex-wrap">
        <Button variant="outline" onClick={onBack} className="gap-1 sm:gap-2 text-sm px-3 sm:px-4">
          <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Kembali</span>
          <span className="sm:hidden">Back</span>
        </Button>
        <Button onClick={handleNext} className="flex-1 gap-1 sm:gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="onboarding-next-btn">
          Lanjut
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </div>
  );
};

// Step 6: Notification Settings
const Step6Notifications = ({ data, setData, onNext, onBack, onSkip }) => {
  const { user } = useAuth();
  const [notifSettings, setNotifSettings] = useState({
    new_order: data.notif_new_order ?? true,
    payment_received: data.notif_payment ?? true,
    // Channels as checkboxes (can select multiple)
    email_enabled: data.notif_email_enabled ?? true,
    telegram_enabled: data.notif_telegram_enabled ?? false,
    telegram_chat_id: data.notif_telegram_chat_id || ''
  });

  const handleNext = () => {
    setData({
      ...data,
      notif_new_order: notifSettings.new_order,
      notif_payment: notifSettings.payment_received,
      // Notifikasi tiap chat masuk dihapus dari onboarding — terlalu berisik untuk email/telegram
      notif_chat: false,
      notif_email_enabled: notifSettings.email_enabled,
      notif_telegram_enabled: notifSettings.telegram_enabled,
      notif_telegram_chat_id: notifSettings.telegram_chat_id
    });
    onNext();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-amber-100 dark:bg-amber-900/30 rounded-xl sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Bell className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">Notifikasi</h2>
        <p className="text-sm sm:text-base text-slate-600 dark:text-zinc-400 mt-2">Kapan Anda ingin menerima notifikasi?</p>
      </div>

      {/* Notification Types */}
      <div className="space-y-2 sm:space-y-3">
        <p className="text-xs sm:text-sm font-medium text-slate-700 dark:text-zinc-300">Jenis Notifikasi:</p>
        
        <div className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg sm:rounded-xl">
          <div>
            <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50"><ShoppingCart className="w-4 h-4 inline mr-1" />Order Baru</p>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Saat ada pesanan masuk</p>
          </div>
          <input
            type="checkbox"
            checked={notifSettings.new_order}
            onChange={(e) => setNotifSettings({ ...notifSettings, new_order: e.target.checked })}
            className="w-4 h-4 sm:w-5 sm:h-5 rounded accent-blue-500"
          />
        </div>

        <div className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg sm:rounded-xl">
          <div>
            <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50"><DollarSign className="w-4 h-4 inline mr-1" />Pembayaran Diterima</p>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Saat customer membayar</p>
          </div>
          <input
            type="checkbox"
            checked={notifSettings.payment_received}
            onChange={(e) => setNotifSettings({ ...notifSettings, payment_received: e.target.checked })}
            className="w-4 h-4 sm:w-5 sm:h-5 rounded accent-blue-500"
          />
        </div>
      </div>

      {/* Notification Channels */}
      <div className="space-y-2 sm:space-y-3 pt-3 sm:pt-4 border-t border-slate-200 dark:border-zinc-800">
        <p className="text-xs sm:text-sm font-medium text-slate-700 dark:text-zinc-300">Kirim notifikasi via (bisa pilih lebih dari satu):</p>
        
        {/* Email Channel */}
        <div className="p-3 sm:p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg sm:rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-100 dark:bg-indigo-950/40 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50"><Mail className="w-4 h-4 inline mr-1" />Email</p>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">{user?.email || 'Email akun Anda'}</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={notifSettings.email_enabled}
              onChange={(e) => setNotifSettings({ ...notifSettings, email_enabled: e.target.checked })}
              className="w-4 h-4 sm:w-5 sm:h-5 rounded accent-blue-500"
            />
          </div>
        </div>

        {/* Telegram Channel */}
        <div className="p-3 sm:p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg sm:rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-sky-100 dark:bg-sky-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-sky-600 dark:text-sky-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50"><Smartphone className="w-4 h-4 inline mr-1" />Telegram</p>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Notifikasi real-time ke Telegram</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={notifSettings.telegram_enabled}
              onChange={(e) => setNotifSettings({ ...notifSettings, telegram_enabled: e.target.checked })}
              className="w-4 h-4 sm:w-5 sm:h-5 rounded accent-blue-500"
            />
          </div>
          
          {notifSettings.telegram_enabled && (
            <div className="mt-2 sm:mt-3 pl-10 sm:pl-13">
              <Label className="text-xs sm:text-sm mb-1 block">Chat ID Telegram</Label>
              <Input
                value={notifSettings.telegram_chat_id}
                onChange={(e) => setNotifSettings({ ...notifSettings, telegram_chat_id: e.target.value })}
                placeholder="Contoh: 123456789"
                className="text-sm"
              />
              <p className="text-[10px] sm:text-xs text-slate-500 mt-1 dark:text-zinc-400">
                <Lightbulb className="w-3 h-3 inline mr-1" />Chat @userinfobot di Telegram untuk mendapatkan Chat ID Anda
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 sm:gap-3 flex-wrap">
        <Button variant="outline" onClick={onBack} className="gap-1 sm:gap-2 text-sm px-3 sm:px-4">
          <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Kembali</span>
          <span className="sm:hidden">Back</span>
        </Button>
        <Button onClick={handleNext} className="flex-1 gap-1 sm:gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="onboarding-next-btn">
          Lanjut
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </div>
  );
};

// Step 7: Follow Up Settings
const Step7FollowUp = ({ data, setData, onNext, onBack, onSkip }) => {
  const [followUp, setFollowUp] = useState({
    enabled: data.followup_enabled ?? true,
    unpaid_enabled: data.followup_unpaid_enabled ?? true,
    unpaid_delay: data.followup_unpaid_delay ?? 2,
    interest_enabled: data.followup_interest_enabled ?? true,
    interest_delay: data.followup_interest_delay ?? 3,
    ai_personalization: data.followup_ai_personalization ?? true
  });

  const handleNext = () => {
    setData({
      ...data,
      followup_enabled: followUp.enabled,
      followup_unpaid_enabled: followUp.unpaid_enabled,
      followup_unpaid_delay: followUp.unpaid_delay,
      followup_interest_enabled: followUp.interest_enabled,
      followup_interest_delay: followUp.interest_delay,
      followup_ai_personalization: followUp.ai_personalization
    });
    onNext();
  };

  const delayOptions = [
    { value: 1, label: '1 jam' },
    { value: 2, label: '2 jam' },
    { value: 3, label: '3 jam' },
    { value: 4, label: '4 jam' },
    { value: 6, label: '6 jam' }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">Follow Up Otomatis</h2>
        <p className="text-sm sm:text-base text-slate-600 dark:text-zinc-400 mt-2">AI akan follow-up customer otomatis dalam 24 jam</p>
      </div>

      {/* Global Toggle */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg sm:rounded-xl border border-indigo-200 dark:border-indigo-800">
        <div>
          <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50">Aktifkan Auto Follow-Up</p>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Pesan dikirim otomatis via channel yang sama</p>
        </div>
        <input
          type="checkbox"
          checked={followUp.enabled}
          onChange={(e) => setFollowUp({ ...followUp, enabled: e.target.checked })}
          className="w-4 h-4 sm:w-5 sm:h-5 rounded accent-indigo-500"
          data-testid="onboarding-followup-toggle"
        />
      </div>

      {followUp.enabled && (
        <div className="space-y-2 sm:space-y-3">
          {/* Belum Bayar */}
          <div className="p-3 sm:p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg sm:rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50">Reminder Belum Bayar</p>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Ingatkan customer yang belum bayar</p>
              </div>
              <input
                type="checkbox"
                checked={followUp.unpaid_enabled}
                onChange={(e) => setFollowUp({ ...followUp, unpaid_enabled: e.target.checked })}
                className="w-4 h-4 sm:w-5 sm:h-5 rounded accent-blue-500"
              />
            </div>
            {followUp.unpaid_enabled && (
              <div className="mt-2">
                <Label className="text-xs mb-1 block">Kirim setelah</Label>
                <Select value={String(followUp.unpaid_delay)} onValueChange={(v) => setFollowUp({ ...followUp, unpaid_delay: parseInt(v) })}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {delayOptions.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Minat Belum Order */}
          <div className="p-3 sm:p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg sm:rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50">Follow Up Minat</p>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Customer tanya produk tapi belum order</p>
              </div>
              <input
                type="checkbox"
                checked={followUp.interest_enabled}
                onChange={(e) => setFollowUp({ ...followUp, interest_enabled: e.target.checked })}
                className="w-4 h-4 sm:w-5 sm:h-5 rounded accent-blue-500"
              />
            </div>
            {followUp.interest_enabled && (
              <div className="mt-2">
                <Label className="text-xs mb-1 block">Kirim setelah</Label>
                <Select value={String(followUp.interest_delay)} onValueChange={(v) => setFollowUp({ ...followUp, interest_delay: parseInt(v) })}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {delayOptions.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* AI Personalization */}
          <div className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg sm:rounded-xl">
            <div>
              <p className="font-medium text-sm sm:text-base text-slate-900 dark:text-zinc-50">AI Personalisasi</p>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">AI sesuaikan pesan dengan konteks chat</p>
            </div>
            <input
              type="checkbox"
              checked={followUp.ai_personalization}
              onChange={(e) => setFollowUp({ ...followUp, ai_personalization: e.target.checked })}
              className="w-4 h-4 sm:w-5 sm:h-5 rounded accent-blue-500"
            />
          </div>
        </div>
      )}

      <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-2.5 sm:p-3">
        <p className="text-xs sm:text-sm text-blue-800 dark:text-indigo-200">
          Pesan follow-up dikirim dalam 24 jam setelah chat terakhir customer (gratis, tanpa biaya tambahan).
        </p>
      </div>

      <div className="flex gap-2 sm:gap-3 flex-wrap">
        <Button variant="outline" onClick={onBack} className="gap-1 sm:gap-2 text-sm px-3 sm:px-4">
          <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">Kembali</span>
          <span className="sm:hidden">Back</span>
        </Button>
        <Button onClick={handleNext} className="flex-1 gap-1 sm:gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="onboarding-next-btn">
          Lanjut
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </div>
  );
};

// Step 8: Complete!
const Step8Complete = ({ data, onFinish, refreshUser, messengerConnected, instagramConnected }) => {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Link WA HARUS diambil dari koneksi yang tersimpan di server (sumber kebenaran yang sama
  // dengan halaman Channel). Dulu keyword dibuat acak di sini sehingga link berubah setiap
  // render dan customer mengirim kode yang tidak dikenal.
  const [waInfo, setWaInfo] = useState({ loading: true, link: '', keyword: '' });

  useEffect(() => {
    const fetchWaLink = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/whatsapp/store-link`, {
          params: { store_name: data.store_name || '' },
          headers: { Authorization: `Bearer ${token}` }
        });
        setWaInfo({
          loading: false,
          link: res.data?.whatsapp_link || '',
          keyword: res.data?.routing_keyword || ''
        });
      } catch (error) {
        setWaInfo({ loading: false, link: '', keyword: '' });
      }
    };
    fetchWaLink();
  }, []);

  const waLink = waInfo.link;

  const copyLink = () => {
    if (!waLink) return;
    navigator.clipboard.writeText(waLink);
    setCopied(true);
    toast.success('Link berhasil disalin!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/complete`, {
        ...data
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      sessionStorage.removeItem('onboarding_step');
      sessionStorage.removeItem('onboarding_data');
      localStorage.setItem('onboarding_completed', 'true');
      toast.success('Setup toko selesai!');
      onFinish();
      if (refreshUser) await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save onboarding:', error?.response?.data || error);
      const detail = error?.response?.data?.detail || 'Unknown error';
      toast.error(`Gagal menyimpan data: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
      sessionStorage.removeItem('onboarding_step');
      sessionStorage.removeItem('onboarding_data');
      navigate('/dashboard');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Check className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">Toko Anda Sudah Siap!</h2>
      </div>

      <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-6 text-center">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-4">
          {data.store_name || 'Toko Anda'}
        </h3>
        <div className="flex flex-wrap justify-center gap-3">
          {data.whatsapp_connected && (
            <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> WhatsApp
            </span>
          )}
          {instagramConnected && (
            <span className="px-3 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400 rounded-full text-sm flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> Instagram
            </span>
          )}
          {messengerConnected && (
            <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 rounded-full text-sm flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> Messenger
            </span>
          )}
          {(data.products?.length || 0) > 0 && (
            <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-full text-sm">
              {data.products.length} Produk
            </span>
          )}
          <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full text-sm">
            AI Aktif
          </span>
        </div>
      </div>

      {waInfo.loading ? (
        <div className="bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-xl p-4 flex items-center gap-2 text-sm text-slate-500 dark:text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Menyiapkan link WhatsApp toko...
        </div>
      ) : waLink ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4" data-testid="onboarding-share-link-box">
          <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
            Share link ini ke customer Anda:
          </p>
          <div className="bg-white dark:bg-zinc-900 rounded-lg p-3 flex items-center gap-2">
            <code className="flex-1 text-sm text-slate-600 dark:text-zinc-300 truncate" data-testid="onboarding-wa-link">
              {waLink}
            </code>
            <Button size="sm" variant="ghost" onClick={copyLink} data-testid="onboarding-copy-link-icon">
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <Button variant="outline" size="sm" className="w-full gap-2 mt-3" onClick={copyLink} data-testid="onboarding-copy-link-btn">
            <Copy className="w-4 h-4" />
            Copy Link
          </Button>
          {waInfo.keyword && (
            <p className="text-xs text-green-700 dark:text-green-300 mt-3">
              Kode toko Anda: <strong>{waInfo.keyword}</strong> — customer yang mengetik kode ini di nomor Nerava langsung tersambung ke toko Anda.
            </p>
          )}
          <p className="text-xs text-green-700 dark:text-green-300 mt-1" data-testid="onboarding-link-placement-hint">
            {(data.business_type || 'physical') === 'digital'
              ? 'Pasang di: Bio Instagram/TikTok, caption konten, Linktree, Website'
              : 'Pasang di: Bio Instagram, Website, Kartu Nama, Kemasan Produk'}
          </p>
        </div>
      ) : (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-200" data-testid="onboarding-no-wa-box">
          WhatsApp belum terhubung. Anda bisa menghubungkannya kapan saja dari menu <strong>Channel</strong> untuk mendapat link toko.
        </div>
      )}

      <Button onClick={handleFinish} disabled={saving} className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="onboarding-finish-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Ke Dashboard
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Main Onboarding Page
const OnboardingPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  // Read step from URL param first (for OAuth redirect), then sessionStorage, default to 1
  const getInitialStep = () => {
    const urlStep = searchParams.get('step');
    if (urlStep) return parseInt(urlStep);
    
    const savedStep = sessionStorage.getItem('onboarding_step');
    if (savedStep) return parseInt(savedStep);
    
    return 1;
  };
  
  const [step, setStep] = useState(getInitialStep());
  const [loading, setLoading] = useState(true);
  const [skipping, setSkipping] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  
  // Initialize data from sessionStorage or defaults
  const [data, setData] = useState(() => {
    try {
      const saved = sessionStorage.getItem('onboarding_data');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {}
    return {
      store_name: '',
      category: '',
      business_type: 'physical',
      whatsapp_connected: false,
      whatsapp_mode: 'nerava',
      whatsapp_phone: '+6285111444413'
    };
  });

  // Channel connection states (lifted from Step2Channels for persistence across OAuth redirects)
  const [messengerConnected, setMessengerConnected] = useState(false);
  const [messengerPageName, setMessengerPageName] = useState('');
  const [instagramConnected, setInstagramConnected] = useState(false);
  const [instagramName, setInstagramName] = useState('');
  const [fbPages, setFbPages] = useState([]);
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [selectingPage, setSelectingPage] = useState(false);
  const [shouldFetchFbPages, setShouldFetchFbPages] = useState(false);

  // Persist step AND data to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem('onboarding_step', step.toString());
  }, [step]);
  
  useEffect(() => {
    sessionStorage.setItem('onboarding_data', JSON.stringify(data));
  }, [data]);

  // Handle OAuth errors/success from URL params
  useEffect(() => {
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    const stepParam = searchParams.get('step');
    const success = searchParams.get('success');
    const username = searchParams.get('username');
    const action = searchParams.get('action');
    
    if (success) {
      // Show success toast for OAuth connections
      if (success === 'instagram_connected') {
        toast.success(`Instagram @${username || ''} berhasil terhubung!`);
      } else if (success === 'messenger_connected') {
        toast.success('Facebook Messenger berhasil terhubung!');
      } else {
        toast.success('Channel berhasil terhubung!');
      }
      // Clean URL params but preserve step
      const currentStep = stepParam || sessionStorage.getItem('onboarding_step') || '2';
      window.history.replaceState({}, '', `/onboarding?step=${currentStep}`);
    }
    
    if (error) {
      // Show error toast but stay on current step
      const errorMessages = {
        'auth_failed': 'Autentikasi gagal. Silakan coba lagi.',
        'no_code': 'Kode autentikasi tidak ditemukan.',
        'token_exchange_failed': 'Gagal mendapatkan token. Silakan coba lagi.',
        'no_access_token': 'Token akses tidak ditemukan.',
        'failed_to_get_pages': 'Gagal mengambil daftar halaman Facebook.',
        'no_pages_found': 'Tidak ada halaman Facebook yang ditemukan. Buat halaman dulu di Facebook.',
        'invalid_state': 'Sesi tidak valid. Silakan coba lagi.',
        'callback_failed': 'Proses autentikasi gagal. Silakan coba lagi.',
        'no_ig_account': 'Akun Instagram Business tidak ditemukan di halaman Facebook Anda.',
        'app_not_configured': 'Aplikasi belum dikonfigurasi dengan benar.'
      };
      
      toast.error(errorMessages[error] || message || `Error: ${error}`);
      
      // Clean URL params but preserve step
      const currentStep = stepParam || sessionStorage.getItem('onboarding_step') || '1';
      window.history.replaceState({}, '', `/onboarding?step=${currentStep}`);
    }
    
    // Handle Facebook page selection step
    if (action === 'select-page') {
      setStep(2);
      setShouldFetchFbPages(true);
      window.history.replaceState({}, '', '/onboarding?step=2');
    } else if (stepParam && !isNaN(parseInt(stepParam))) {
      // Restore step from URL
      setStep(parseInt(stepParam));
    }
  }, [searchParams]);

  // Clear sessionStorage when onboarding is completed
  const clearOnboardingState = () => {
    sessionStorage.removeItem('onboarding_step');
    sessionStorage.removeItem('onboarding_data');
  };

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);
  const handleSkip = () => setStep(step + 1);
  const handleFinish = () => {
    clearOnboardingState();
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.removeItem('onboarding_skipped');
  };

  // Skip entire tour and go directly to dashboard
  const handleSkipTour = async () => {
    setSkipping(true);
    try {
      const token = localStorage.getItem('token');
      
      // If user has entered any data, save it first before skipping
      if (data.store_name || data.category || data.description || data.address) {
        await axios.post(`${API}/onboarding/complete`, {
          ...data,
          skipped: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // No data entered, just mark as skipped
        await axios.post(`${API}/onboarding/skip`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      clearOnboardingState();
      localStorage.setItem('onboarding_skipped', 'true');
      localStorage.setItem('onboarding_completed', 'true');
      toast.success('Data tersimpan! Anda bisa melengkapi setup nanti di Settings');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      // Still allow navigation even if API fails
      clearOnboardingState();
      localStorage.setItem('onboarding_skipped', 'true');
      localStorage.setItem('onboarding_completed', 'true');
      navigate('/dashboard');
    } finally {
      setSkipping(false);
      setShowSkipDialog(false);
    }
  };

  // Check authentication and onboarding status
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        // Not logged in, redirect to login page
        navigate('/login');
        return;
      }
      
      // Check from backend first (source of truth)
      try {
        const response = await axios.get(`${API}/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data?.completed) {
          localStorage.setItem('onboarding_completed', 'true');
          navigate('/dashboard');
          return;
        }
        
        // Not completed — pre-fill data from user profile if sessionStorage is empty
        const savedData = sessionStorage.getItem('onboarding_data');
        if (!savedData || savedData === '{}') {
          try {
            const meRes = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const u = meRes.data?.user || meRes.data;
            if (u && (u.store_name || u.business_category)) {
              setData(prev => ({
                ...prev,
                store_name: u.store_name || prev.store_name,
                category: u.business_category || prev.category,
                description: u.business_description || prev.description,
                street_address: u.street_address || prev.street_address,
                city_name: u.city_name || prev.city_name,
                district_name: u.district_name || prev.district_name,
                subdistrict_name: u.subdistrict_name || prev.subdistrict_name,
                province_name: u.province_name || prev.province_name,
                postal_code: u.postal_code || prev.postal_code,
                destination_id: u.destination_id || prev.destination_id,
              }));
            }
          } catch (e) {}
        }
        
        // Show onboarding wizard
        setLoading(false);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // If 401 unauthorized, redirect to login
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        // For other errors, still show onboarding to prevent blocking users
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin dark:text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-start sm:items-center justify-center p-3 sm:p-4 py-6 sm:py-4 overflow-x-hidden">
      <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-4 sm:p-6 md:p-8">
        {/* Skip Tour Button - Only show on steps 1-6 */}
        {step < TOTAL_STEPS && (
          <>
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setShowSkipDialog(true)}
                className="text-sm text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors flex items-center gap-1 dark:text-zinc-400"
                data-testid="skip-tour-btn"
              >
                {t('onboarding.skip')}
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
            <ProgressBar currentStep={step} totalSteps={TOTAL_STEPS} t={t} />
          </>
        )}

        {step === 1 && (
          <Step1Business data={data} setData={setData} onNext={handleNext} t={t} />
        )}
        {step === 2 && (
          <Step2Channels data={data} setData={setData} onNext={handleNext} onBack={handleBack} t={t}
            fbPages={fbPages} setFbPages={setFbPages}
            showPageSelector={showPageSelector} setShowPageSelector={setShowPageSelector}
            selectingPage={selectingPage} setSelectingPage={setSelectingPage}
            shouldFetchFbPages={shouldFetchFbPages} setShouldFetchFbPages={setShouldFetchFbPages}
            messengerConnected={messengerConnected} setMessengerConnected={setMessengerConnected}
            messengerPageName={messengerPageName} setMessengerPageName={setMessengerPageName}
            instagramConnected={instagramConnected} setInstagramConnected={setInstagramConnected}
            instagramName={instagramName} setInstagramName={setInstagramName}
          />
        )}
        {step === 3 && (
          <Step3Products data={data} setData={setData} onNext={handleNext} onBack={handleBack} onSkip={handleSkip} t={t} />
        )}
        {step === 4 && (
          <Step4Payment data={data} setData={setData} onNext={handleNext} onBack={handleBack} onSkip={handleSkip} t={t} />
        )}
        {step === 5 && (
          <Step5AI data={data} setData={setData} onNext={handleNext} onBack={handleBack} onSkip={handleSkip} t={t} />
        )}
        {step === 6 && (
          <Step6Notifications data={data} setData={setData} onNext={handleNext} onBack={handleBack} onSkip={handleSkip} t={t} />
        )}
        {step === 7 && (
          <Step7FollowUp data={data} setData={setData} onNext={handleNext} onBack={handleBack} onSkip={handleSkip} t={t} />
        )}
        {step === 8 && (
          <Step8Complete data={data} onFinish={handleFinish} refreshUser={refreshUser} t={t} messengerConnected={messengerConnected} instagramConnected={instagramConnected} />
        )}
      </div>

      {/* Skip Tour Confirmation Dialog */}
      <Dialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Lewati Setup?
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-slate-600 dark:text-zinc-400">
              Anda bisa langsung ke dashboard, tapi beberapa fitur mungkin belum optimal tanpa setup lengkap:
            </p>
            <ul className="text-sm text-slate-500 space-y-2 dark:text-zinc-400">
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <span>AI Agent belum dikonfigurasi dengan info bisnis Anda</span>
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <span>WhatsApp belum aktif untuk menerima chat customer</span>
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <span>Katalog produk kosong</span>
              </li>
            </ul>
            <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-2.5 sm:p-3">
              <p className="text-xs sm:text-sm text-indigo-700 dark:text-indigo-300">
                Anda bisa melengkapi setup kapan saja di menu <strong>Settings</strong>
              </p>
            </div>
          </div>

          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => setShowSkipDialog(false)}
              className="flex-1 text-sm"
            >
              Lanjut Setup
            </Button>
            <Button
              onClick={handleSkipTour}
              disabled={skipping}
              className="flex-1 bg-slate-600 hover:bg-slate-700 text-sm"
              data-testid="confirm-skip-btn"
            >
              {skipping ? (
                <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
              ) : (
                <>
                  Ke Dashboard
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingPage;
